import i18n from "../../i18n";
import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { type ReceivablesFormData } from "../../types/receivables.types";
import { receivableApi } from "../../api/receivableApi";
import { createResolvedMedia, uploadMediaFiles } from "../../utils/mediaUtils";
import { getCurrentUser } from "../../auth/auth.service";
import { getApiErrorMessage } from "../../utils/apiError";
import { toast } from "../../components/common/toast";
import type { AppDispatch, RootState } from "../../app/store";
import { mediaLinkKey, resolveMediaLinks } from "../../utils/mediaLinkBatch";

const emptyFormState: ReceivablesFormData = {
  connectedContractNumber: "",
  connectedContractLabel: "",
  contractNumber: "",
  jobOrder: "",
  businessUnit: "",
  department: "",
  businessPointOfContact: "",
  mediaFilesCount: 0,
  requestingUser: {
    name: "",
    email: "",
    phone: "",
    designation: "",
    businessDepartment: "",
  },
  debtorDetails: {
    debtorCategory: "Entity",
    entityType: "",
    registeredName: "",
    authorisedRepresentativeName: "",
    authorisedRepresentativeEmail: "",
    registeredAddress: "",
    corporateAddress: "",
    selectedOrgId: "",
    fullName: "",
    email: "",
    phone: "",
    address: "",
  },
  invoices: [],
  actions: [],
  roles: {
    initiators: [],
    editors: [],
    approvers: [],
  },
};

interface ReceivablesState {
  list: ReceivablesFormData[];
  selectedItem: ReceivablesFormData | null;
  mode: "create" | "view" | "edit";
  isReadOnly: boolean;
  loading: boolean;
  error: string | null;
  pageIndex: number;
  hasMore: boolean;
  totalReceivables: number;
}

const initialState: ReceivablesState = {
  list: [],
  selectedItem: null,
  mode: "create",
  isReadOnly: false,
  loading: false,
  error: null,
  pageIndex: 0,
  hasMore: true,
  totalReceivables: 0,
};

export const fetchReceivables = createAsyncThunk(
  "receivables/fetchReceivables",
  async (payload: any = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { receivables: ReceivablesState };

      const incomingPageIndex =
        payload.pageIndex ?? state.receivables.pageIndex;

      if (!state.receivables.hasMore && incomingPageIndex !== 0) {
        return rejectWithValue("__SKIP__");
      }

      const requestBody = {
        pageSize: payload.pageSize || 10,
        pageIndex: incomingPageIndex,
        orderBy: payload.orderBy || "modificationdate",
        sortAscending: payload.sortAscending ?? false,
        includeFacets: payload.includeFacets ?? true,
        groupIds: payload.groupIds || [],
        ids: payload.ids || [],
        assignedMemberIds: payload.assignedMemberIds || [],
        includeDeleted: payload.includeDeleted ?? false,
        departmentIds: payload.departmentIds || [],
        isActive: payload.isActive ?? null,
        createdBy: payload.createdBy || [],
        updatedBy: payload.updatedBy || [],
        myMode: payload.myMode ?? true,
        searchText: payload.searchText || "",

        ...(payload.currentProfileId !== undefined && {
          currentProfileId: payload.currentProfileId,
        }),

        ...(payload.startDate && {
          startDate: payload.startDate,
        }),

        ...(payload.endDate && {
          endDate: payload.endDate,
        }),

        ...(payload.statuses?.length && {
          statuses: payload.statuses,
        }),
      };

      console.log("[Receivables] search request:", requestBody);

      const response = await receivableApi.searchReceivables(requestBody);

      const fetchedData = response?.data || [];
      const totalCount =
        response?.count ??
        response?.totalCount ??
        response?.total ??
        fetchedData.length;

      // One media request per organisation for the page, not one per
      // invoice of every receivable.
      const ATTACHMENT_MEDIA_TYPE = 3;
      const links = await resolveMediaLinks(
        fetchedData as any[],
        (receivable, want) => {
          for (const invoice of receivable.invoices || []) {
            for (const media of invoice?.medias || []) {
              want(receivable.groupId, ATTACHMENT_MEDIA_TYPE, media?.s3Url);
            }
          }
        },
        receivableApi.getInvoiceMediaLink,
        "Receivable invoice",
      );

      const resolvedData = fetchedData.map((receivable: any) => {
        if (!Array.isArray(receivable.invoices) || !receivable.invoices.length) {
          return { ...receivable };
        }
        return {
          ...receivable,
          invoices: receivable.invoices.map((invoice: any) => {
            const hasMedia =
              Array.isArray(invoice.medias) &&
              invoice.medias.some((media: any) => media?.s3Url);
            if (!hasMedia) return invoice;
            return {
              ...invoice,
              medias: invoice.medias.map((media: any) => ({
                ...media,
                url:
                  links.get(mediaLinkKey(ATTACHMENT_MEDIA_TYPE, media?.s3Url)) ||
                  "",
              })),
              mediaLinks: null,
            };
          }),
        };
      });

      return {
        data: resolvedData as ReceivablesFormData[],
        totalCount,
        pageIndex: incomingPageIndex,
      };
    } catch (err: any) {
      console.error("Fetch Receivables error:", err);

      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          i18n.t("slices.receivables.fetchListError", { ns: "common" }),
      );
    }
  },
);

export const getReceivableById = createAsyncThunk(
  "receivables/getReceivableById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await receivableApi.searchReceivables({
        ids: [id],
        pageIndex: 0,
        pageSize: 1,
      });

      const data = response?.data ?? response;

      if (Array.isArray(data)) {
        return data[0] ?? null;
      }

      if (Array.isArray(data?.items)) {
        return data.items[0] ?? null;
      }

      return data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          i18n.t("slices.receivables.fetchError", { ns: "common" }),
      );
    }
  },
);

export const addReceivable = createAsyncThunk(
  "receivables/addReceivable",
  async (newData: ReceivablesFormData, { rejectWithValue }) => {
    try {
      const response = await receivableApi.addReceivable(newData);
      const responseData = response?.data || response?.result || response || {};

      return {
        ...newData,
        ...responseData,
        contractNumber:
          responseData?.contractNumber || newData.contractNumber || "",
      } as ReceivablesFormData;
    } catch (err: any) {
      console.error("Add Receivable error:", err);
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          i18n.t("slices.receivables.addError", { ns: "common" }),
      );
    }
  },
);

export const updateReceivable = createAsyncThunk(
  "receivables/updateReceivable",
  async (updatedData: ReceivablesFormData, { rejectWithValue }) => {
    try {
      const response = await receivableApi.editReceivable(updatedData);
      const responseData = response?.data || response?.result || response || {};

      return {
        ...updatedData,
        ...responseData,
        contractNumber:
          responseData?.contractNumber || updatedData.contractNumber,
      } as ReceivablesFormData;
    } catch (err: any) {
      console.error("Update Receivable error:", err);
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          i18n.t("slices.receivables.editError", { ns: "common" }),
      );
    }
  },
);

export const deleteReceivable = createAsyncThunk(
  "receivables/deleteReceivable",
  async (contractNumber: string, { rejectWithValue }) => {
    try {
      await receivableApi.removeReceivable(contractNumber);
      return contractNumber;
    } catch (err: any) {
      console.error("Delete Receivable error:", err);
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          i18n.t("slices.receivables.removeError", { ns: "common" }),
      );
    }
  },
);
export const uploadReceivableMedias = (receivableId: string, files: File[]) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!files?.length) return;

      const state = getState();
      const userId = (await getCurrentUser()).userId;

      const currentReceivable = state.receivables.list.find(
        (item: ReceivablesFormData) => item.contractNumber === receivableId,
      );

      const groupId =
        (currentReceivable as any)?.groupId ||
        (currentReceivable as any)?.organisationId ||
        undefined;

      const { payloads, links } = await uploadMediaFiles(
        receivableApi,
        receivableId,
        files,
        groupId,
      );

      const newEntries = createResolvedMedia(
        payloads,
        links,
        receivableId,
        userId,
      );

      const updatedList = state.receivables.list.map(
        (item: ReceivablesFormData) =>
          item.contractNumber === receivableId
            ? {
                ...item,
                medias: [...((item as any).medias || []), ...newEntries],
                mediaLinks: null,
              }
            : item,
      );

      dispatch(
        updateCurrentFormData({
          medias: [
            ...((currentReceivable as any)?.medias || []),
            ...newEntries,
          ],
          mediaLinks: null,
        } as any),
      );

      dispatch(setReceivables(updatedList));

      toast.success(` ${files.length} files uploaded successfully.`);
    } catch (err) {
      console.error("Receivable Upload Error:", err);

      const message = getApiErrorMessage(err, i18n.t("slices.receivables.uploadError", { ns: "common" }));

      toast.error(message);
      throw new Error(message);
    }
  };
};

const getFileType = (file: File): string => {
  const ext = file.name.split(".").pop()?.toLowerCase() || "";

  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  if (file.type.startsWith("audio/")) return "audio";

  if (
    file.type.includes("pdf") ||
    file.type.includes("document") ||
    ["pdf", "doc", "docx", "xls", "xlsx", "txt"].includes(ext)
  ) {
    return "document";
  }

  return "other";
};

export const prepareReceivableInvoiceMedias = async (
  files: File[],
  groupId?: string,
) => {
  if (!files?.length) return [];

  const fileIds = files.map(() => crypto.randomUUID());

  const initResponse = await receivableApi.initUploadInvoice(fileIds, groupId);
  const links = initResponse?.links || [];
  const payloads =
    initResponse?.payloads ||
    initResponse?.data?.payloads ||
    initResponse?.data ||
    [];

  await Promise.all(
    files.map(async (file, index) => {
      const fileId = fileIds[index];
      const link =
        links.find((item: any) => item?.id === fileId) || links[index];
      const uploadUrl = link?.t || link?.url;

      if (!uploadUrl) {
        throw new Error(`Upload URL not found for ${file.name}`);
      }

      await receivableApi.uploadToS3Invoice(uploadUrl, file);
    }),
  );

  return files.map((file, index) => {
    const fileId = fileIds[index];
    const link =
      links.find((item: any) => item?.id === fileId) || links[index] || {};
    const resolved =
      payloads.find((item: any) => item?.id === fileId)?.payload ||
      payloads[index]?.payload ||
      payloads.find((item: any) => item?.id === fileId) ||
      {};

    return {
      id: fileId,
      requestType: Number(resolved.requestType ?? 1),
      fileName: resolved.fileName || file.name,
      originalFileName: resolved.originalFileName || file.name,
      fileType: resolved.fileType || file.type || "",
      mimeType: resolved.mimeType || file.type || "",
      fileSize: Number(resolved.fileSize ?? file.size ?? 0),

      s3Url: resolved.s3Url || resolved.key || link.s3Url || link.key || fileId,
      bucket: Number(resolved.bucket ?? 0),
      bucketName: resolved.bucketName || "",
      module: Number(resolved.module ?? 0),
      moduleName: resolved.moduleName || "",
      entityId: "3fa85f64-5717-4562-b3fc-2c963f66afa6",
      tags: resolved.tags || [],
      metadata: resolved.metadata || "",
      isProfilePic: Boolean(resolved.isProfilePic),
      mediaType: Number(resolved.mediaType ?? 0),
    };
  });
};

export const uploadReceivableInvoiceMedias = (
  receivableId: string,
  invoiceId: string,
  files: File[],
) => {
  return async (getState: () => RootState) => {
    try {
      if (!files?.length || !invoiceId) return [];

      const state = getState();

      const currentReceivable = state.receivables.list.find(
        (item: any) =>
          item.id === receivableId ||
          item.receivableId === receivableId ||
          item.contractNumber === receivableId,
      );

      const groupId =
        (currentReceivable as any)?.groupId ||
        (currentReceivable as any)?.organisationId ||
        undefined;

      const fileIds = files.map(() => crypto.randomUUID());

      const initResponse = await receivableApi.initUploadInvoice(
        fileIds,
        groupId,
      );

      const links = initResponse?.links || [];

      await Promise.all(
        files.map(async (file, index) => {
          const uploadLink = links.find(
            (link: any) => link.id === fileIds[index],
          );

          const uploadUrl = uploadLink?.t;

          if (!uploadUrl) {
            throw new Error(`S3 upload URL not found for ${file.name}`);
          }

          await receivableApi.uploadToS3Invoice(uploadUrl, file);
        }),
      );

      const medias = files.map((file, index) => ({
        id: fileIds[index],
        requestType: 1,
        fileName: file.name,
        originalFileName: file.name,
        fileType: getFileType(file),
        mimeType: file.type || getFileType(file),
        fileSize: file.size,
        s3Url: fileIds[index],
        bucket: 0,
        bucketName: "",
        module: 0,
        moduleName: "",
        entityId: invoiceId,
        tags: [],
        metadata: "",
        isProfilePic: false,
        mediaType: 1,
      }));

      await receivableApi.uploadMediaInvoice(invoiceId, medias);

      toast.success(`${files.length} invoice files uploaded successfully.`);

      return medias;
    } catch (err) {
      console.error("Receivable Invoice Upload Error:", err);

      const message = getApiErrorMessage(err, i18n.t("slices.receivables.invoiceUploadError", { ns: "common" }));

      toast.error(message);
      throw new Error(message);
    }
  };
};

export const handleActivateReceivable = (id: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await receivableApi.activateReceivable(id);

      const { list, selectedItem } = getState().receivables;

      const updatedList = list.map((item: ReceivablesFormData) =>
        (item as any).id === id || item.contractNumber === id
          ? ({ ...item, isActive: true } as ReceivablesFormData)
          : item,
      );

      dispatch(setReceivables(updatedList));

      if (
        selectedItem &&
        ((selectedItem as any).id === id || selectedItem.contractNumber === id)
      ) {
        dispatch(
          updateCurrentFormData({
            isActive: true,
          } as any),
        );
      }

      toast.success(i18n.t("slices.receivables.activateSuccess", { ns: "common" }));
    } catch (err: any) {
      console.error("Activate Receivable error:", err);

      const message = getApiErrorMessage(err, i18n.t("slices.receivables.activateError", { ns: "common" }));

      toast.error(message);
      throw new Error(message);
    }
  };
};

export const handleDeactivateReceivable = (id: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await receivableApi.deactivateReceivable(id);

      const { list, selectedItem } = getState().receivables;

      const updatedList = list.map((item: ReceivablesFormData) =>
        (item as any).id === id || item.contractNumber === id
          ? ({ ...item, isActive: false } as ReceivablesFormData)
          : item,
      );

      dispatch(setReceivables(updatedList));

      if (
        selectedItem &&
        ((selectedItem as any).id === id || selectedItem.contractNumber === id)
      ) {
        dispatch(
          updateCurrentFormData({
            isActive: false,
          } as any),
        );
      }

      toast.success(i18n.t("slices.receivables.deactivateSuccess", { ns: "common" }));
    } catch (err: any) {
      console.error("Deactivate Receivable error:", err);

      const message = getApiErrorMessage(
        err,
        i18n.t("slices.receivables.deactivateError", { ns: "common" }),
      );

      toast.error(message);
      throw new Error(message);
    }
  };
};

export const handleChangeReceivableStatus = createAsyncThunk(
  "receivables/changeStatus",
  async (
    payload: {
      receivableId: string;
      status: number;
    },
    { rejectWithValue },
  ) => {
    try {
      await receivableApi.changeStatusReceivable(payload);
      return payload;
    } catch (err: any) {
      console.error("Change Receivable Status error:", err);
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          i18n.t("slices.receivables.statusError", { ns: "common" }),
      );
    }
  },
);

export const receivablesSlice = createSlice({
  name: "receivables",
  initialState,
  reducers: {
    setCreateMode: (state) => {
      state.mode = "create";
      state.isReadOnly = false;
      state.selectedItem = { ...emptyFormState };
    },

    setEditMode: (state, action: PayloadAction<ReceivablesFormData>) => {
      state.mode = "edit";
      state.isReadOnly = false;
      state.selectedItem = action.payload;
    },

    setViewMode: (state, action: PayloadAction<ReceivablesFormData>) => {
      state.mode = "view";
      state.isReadOnly = true;
      state.selectedItem = action.payload;
    },

    updateCurrentFormData: (
      state,
      action: PayloadAction<Partial<ReceivablesFormData>>,
    ) => {
      if (state.selectedItem) {
        state.selectedItem = {
          ...state.selectedItem,
          ...action.payload,
        };
      }
    },

    resetFormState: (state) => {
      state.mode = "create";
      state.isReadOnly = false;
      state.selectedItem = { ...emptyFormState };
    },

    setReceivables: (state, action: PayloadAction<ReceivablesFormData[]>) => {
      state.list = action.payload;
    },

    resetReceivableList: (state) => {
      state.list = [];
      state.pageIndex = 0;
      state.hasMore = true;
      state.totalReceivables = 0;
    },

    setReceivableActiveState: (
      state,
      action: PayloadAction<{ receivableId: string; isActive: boolean }>,
    ) => {
      const { receivableId, isActive } = action.payload;

      state.list = state.list.map((item) =>
        (item as any).id === receivableId ||
        item.contractNumber === receivableId
          ? ({ ...item, isActive } as ReceivablesFormData)
          : item,
      );

      if (
        state.selectedItem &&
        ((state.selectedItem as any).id === receivableId ||
          state.selectedItem.contractNumber === receivableId)
      ) {
        state.selectedItem = {
          ...state.selectedItem,
          isActive,
        } as ReceivablesFormData;
      }
    },

    clearReceivableError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder

      .addCase(fetchReceivables.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReceivables.fulfilled, (state, action) => {
        state.loading = false;

        const { data, totalCount, pageIndex } = action.payload;

        state.totalReceivables = totalCount;

        if (pageIndex === 0) {
          state.list = data;
          state.pageIndex = 1;
        } else {
          const existingIds = new Set(
            state.list.map(
              (item: any) =>
                item?.id || item?.receivableId || item?.contractNumber,
            ),
          );

          const newItems = data.filter((item: any) => {
            const itemId =
              item?.id || item?.receivableId || item?.contractNumber;
            return itemId ? !existingIds.has(itemId) : true;
          });

          state.list = [...state.list, ...newItems];
          state.pageIndex = pageIndex + 1;
        }

        state.hasMore = state.list.length < totalCount && data.length >= 10;
      })
      .addCase(fetchReceivables.rejected, (state, action) => {
        state.loading = false;

        if (action.payload === "__SKIP__") return;

        state.error =
          (action.payload as string) || i18n.t("slices.receivables.fetchListError", { ns: "common" });
      })

      .addCase(getReceivableById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getReceivableById.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        const item = action.payload;

        if (!item) {
          state.selectedItem = null;
          return;
        }

        state.selectedItem = item;
        state.mode = "edit";
        state.isReadOnly = false;
      })
      .addCase(getReceivableById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          action.error.message ||
          i18n.t("slices.receivables.fetchError", { ns: "common" });
        state.selectedItem = null;
      })

      .addCase(addReceivable.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addReceivable.fulfilled, (state, action) => {
        state.loading = false;
        state.list.unshift(action.payload);
        state.selectedItem = { ...emptyFormState };
        state.mode = "create";
        state.isReadOnly = false;
      })
      .addCase(addReceivable.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || i18n.t("slices.receivables.addError", { ns: "common" });
      })

      .addCase(updateReceivable.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateReceivable.fulfilled, (state, action) => {
        state.loading = false;

        const index = state.list.findIndex(
          (item) => item.contractNumber === action.payload.contractNumber,
        );

        if (index !== -1) {
          state.list[index] = action.payload;
        }

        state.selectedItem = action.payload;
      })
      .addCase(updateReceivable.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || i18n.t("slices.receivables.editError", { ns: "common" });
      })

      .addCase(deleteReceivable.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteReceivable.fulfilled, (state, action) => {
        state.loading = false;

        state.list = state.list.filter(
          (item) => item.contractNumber !== action.payload,
        );

        if (state.selectedItem?.contractNumber === action.payload) {
          state.selectedItem = { ...emptyFormState };
          state.mode = "create";
          state.isReadOnly = false;
        }
      })
      .addCase(deleteReceivable.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || i18n.t("slices.receivables.removeError", { ns: "common" });
      })

      .addCase(handleChangeReceivableStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(handleChangeReceivableStatus.fulfilled, (state, action) => {
        state.loading = false;

        const { receivableId, status } = action.payload;

        state.list = state.list.map((item) =>
          item.contractNumber === receivableId
            ? ({ ...item, status } as ReceivablesFormData)
            : item,
        );

        if (state.selectedItem?.contractNumber === receivableId) {
          state.selectedItem = {
            ...state.selectedItem,
            status,
          } as ReceivablesFormData;
        }
      })
      .addCase(handleChangeReceivableStatus.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || i18n.t("slices.receivables.statusError", { ns: "common" });
      });
  },
});

export const {
  setCreateMode,
  setEditMode,
  setViewMode,
  updateCurrentFormData,
  resetFormState,
  resetReceivableList,
  clearReceivableError,
  setReceivables,
  setReceivableActiveState,
} = receivablesSlice.actions;

export default receivablesSlice.reducer;
